/**
 * Sync Center actions (Phase 16C C8, design §8.6 client column).
 *
 * A corrected op is always queued under a NEW op id: the server's idempotency guard ties an op id
 * to its first body, so resending a changed body under the old id would be IDEMPOTENCY_KEY_REUSED.
 */
import type { Outbox, OutboxOp } from './outbox';
import type { ChartRepo } from './repos/chartRepo';
import type { AssessmentRepo } from './repos/assessmentRepo';

export interface ResolutionDeps {
  outbox: Outbox;
  charts: ChartRepo;
  assessments: AssessmentRepo;
}

/** The server's edit version (16B `editVersion`; `version` is the amendment number). */
const versionOf = (current: OutboxOp['current']): number | undefined => {
  if (!current) return undefined;
  if (typeof current.editVersion === 'number') return current.editVersion;
  return typeof current.version === 'number' ? current.version : undefined;
};

/** Resend the op as it is (needs_attention that may pass now, e.g. after the office fixed a setting). */
export async function retryOp(d: ResolutionDeps, op: OutboxOp): Promise<void> {
  await d.outbox.update(op.opId, { status: 'pending', errorCode: undefined, lastError: undefined });
}

/** Conflict → keep my version: resend against the server's version. */
export async function keepMine(d: ResolutionDeps, op: OutboxOp): Promise<OutboxOp> {
  await d.outbox.remove(op.opId);
  return d.outbox.enqueue({
    type: op.type,
    patientId: op.patientId,
    visitId: op.visitId,
    entityId: op.entityId,
    payload: op.payload,
    expectedVersion: versionOf(op.current) ?? op.expectedVersion,
    observedAt: op.observedAt,
    capturedAt: op.capturedAt,
  });
}

/** Conflict → keep the server's version: drop my change and take theirs into the device copy. */
export async function keepServer(d: ResolutionDeps, op: OutboxOp): Promise<void> {
  await d.outbox.remove(op.opId);
  const current = op.current || {};
  if (!op.entityId) return;
  if (op.type.startsWith('CHART_')) {
    const entry = await d.charts.get(op.entityId);
    if (entry) {
      await d.charts.put({
        ...entry,
        ...(current.data !== undefined ? { data: current.data } : {}),
        ...(typeof current.entryType === 'string' ? { entryType: current.entryType } : {}),
        serverVersion: versionOf(current) ?? entry.serverVersion,
        syncState: 'synced',
      });
    }
  } else if (op.type.startsWith('ASSESSMENT_')) {
    const a = await d.assessments.get(op.entityId);
    if (a) await d.assessments.put({ ...a, body: { ...a.body, ...(current.data ? { data: current.data } : {}) }, serverVersion: versionOf(current) ?? a.serverVersion, syncState: 'synced' });
  }
}

/** TIME_* / VALIDATION on a chart op: correct the observed time and resend. */
export async function fixObservedTime(d: ResolutionDeps, op: OutboxOp, observedAt: string): Promise<OutboxOp> {
  await d.outbox.remove(op.opId);
  if (op.entityId && op.type.startsWith('CHART_')) {
    const entry = await d.charts.get(op.entityId);
    if (entry) await d.charts.put({ ...entry, observedAt, syncState: 'pending' });
  }
  return d.outbox.enqueue({
    type: op.type,
    patientId: op.patientId,
    visitId: op.visitId,
    entityId: op.entityId,
    payload: op.payload,
    expectedVersion: op.expectedVersion,
    observedAt,
  });
}

/** Held for supervisor review: the server keeps the op in quarantine; the device copy can go. */
export async function acknowledgeHeld(d: ResolutionDeps, op: OutboxOp): Promise<void> {
  await d.outbox.remove(op.opId);
}

/** Plain-language explanation for a result code. */
export function describeCode(code?: string): string {
  switch (code) {
    case 'QUARANTINED': return 'Held for supervisor review. The office has it and will import or follow up; nothing is lost.';
    case 'INVALID_TRANSITION': return 'This chart was already submitted or finalized, so the change was not applied.';
    case 'CONFLICT':
    case 'CONCURRENT_UPDATE': return 'This was changed somewhere else (another device) after you edited it.';
    case 'TIME_IN_FUTURE': return 'The time on this device was ahead of the server. Check the device clock.';
    case 'TIME_TOO_OLD': return 'This was captured too long ago to sync on its own. It is held for supervisor review.';
    case 'TIME_ORDER': return 'The time is outside the visit, or the visit end is before its start.';
    case 'VALIDATION': return 'The server could not accept this item as entered.';
    case 'DEPENDENCY_FAILED': return 'Waiting for an earlier item of this visit.';
    case 'IDEMPOTENCY_KEY_REUSED': return 'This item was sent before with different content. Contact support; your data is kept.';
    case 'ESCROW_KEY_UNKNOWN':
    case 'ESCROW_DECRYPT_FAILED': return 'A protected copy could not be opened. Contact support.';
    default: return code ? `Not accepted (${code}).` : 'Waiting to sync.';
  }
}

export const OP_LABELS: Record<OutboxOp['type'], string> = {
  VISIT_START: 'Visit start',
  VISIT_END: 'Visit end and signature',
  VISIT_CANCEL: 'Visit cancellation',
  CHART_CREATE: 'Chart entry',
  CHART_UPDATE: 'Chart entry change',
  CHART_SUBMIT: 'Chart submission',
  ASSESSMENT_CREATE: 'Assessment',
  ASSESSMENT_UPDATE: 'Assessment changes',
  ASSESSMENT_COMPLETE: 'Assessment completion',
};
